import React, { useEffect, useRef, useState, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { Smartphone, Loader2, RefreshCw, Plug, CheckCircle, KeyRound, Unplug, Eye, EyeOff, Lock, ExternalLink, ToggleLeft, ToggleRight, Zap, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const TaptileApp = () => {
  const [embedUrl, setEmbedUrl] = useState(null);
  const [embedState, setEmbedState] = useState('idle'); // idle | loading | ready | error
  const [embedError, setEmbedError] = useState('');
  const timerRef = useRef(null);

  // Settings state
  const [settings, setSettings] = useState(null); // null = loading, {} = loaded
  const [showSettings, setShowSettings] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [apiUrl, setApiUrl] = useState('https://taptile.app');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Provisioning state
  const [provisioning, setProvisioning] = useState(false);
  const [disablingProvision, setDisablingProvision] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [retrievingKey, setRetrievingKey] = useState(false);

  // 1. Load settings first
  const loadSettings = useCallback(async () => {
    try {
      const res = await api.get('/integrations/taptile/settings');
      setSettings(res.data);
      setApiUrl(res.data.api_url || 'https://taptile.app');
    } catch {
      setSettings({ connected: false });
    }
  }, []);

  // 2. Try to get embed token (only when connected)
  const loadEmbed = useCallback(async () => {
    setEmbedState('loading');
    setEmbedError('');
    try {
      clearTimeout(timerRef.current);
      const res = await api.post('/integrations/taptile/embed-token');
      setEmbedUrl(res.data.embed_url);
      setEmbedState('ready');
      const ms = Math.max((res.data.expires_in || 300) - 30, 30) * 1000;
      timerRef.current = setTimeout(loadEmbed, ms);
    } catch (e) {
      setEmbedState('error');
      setEmbedError(e?.response?.data?.detail || 'Could not load Taptile embed.');
    }
  }, []);

  useEffect(() => {
    loadSettings();
    return () => clearTimeout(timerRef.current);
  }, [loadSettings]);

  // Auto-load embed once we know we're connected
  useEffect(() => {
    if (settings?.connected && embedState === 'idle') {
      loadEmbed();
    }
  }, [settings, embedState, loadEmbed]);

  const handleSave = async () => {
    if (!apiKey.trim()) { toast.error('API key is required'); return; }
    setSaving(true);
    try {
      await api.post('/integrations/taptile/settings', { api_key: apiKey.trim(), api_url: apiUrl });
      toast.success('Taptile connected');
      setApiKey('');
      setShowSettings(false);
      await loadSettings();
      setEmbedState('idle'); // trigger embed load
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Taptile? Events push notifications and the embed will stop working.')) return;
    setDisconnecting(true);
    try {
      await api.delete('/integrations/taptile/settings');
      toast.success('Taptile disconnected');
      setSettings({ connected: false });
      setEmbedState('idle');
      setEmbedUrl(null);
      clearTimeout(timerRef.current);
    } catch {
      toast.error('Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleProvision = async () => {
    setProvisioning(true);
    try {
      const res = await api.post('/integrations/taptile/provision');
      const isNew = res.data.status === 'created';
      const inviteSent = res.data.invite_sent;
      if (!isNew) {
        toast.success('Taptile workspace re-enabled.');
      } else if (inviteSent) {
        toast.success('Taptile workspace created — check your email for the invite link.');
      } else {
        toast.success('Taptile workspace created. Use the portal link to access your account.');
      }
      await loadSettings();

      // If Taptile didn't return the API key in the provision response, try to
      // retrieve it automatically so the amber card never appears.
      if (!res.data.connected) {
        try {
          await api.post('/integrations/taptile/retrieve-api-key');
          toast.success('API key retrieved automatically!');
          await loadSettings();
          setEmbedState('idle');
        } catch {
          // Auto-retrieve failed — amber card will show with "Enter manually" option
          setEmbedState('idle');
        }
      } else {
        setEmbedState('idle');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to enable Taptile');
    } finally {
      setProvisioning(false);
    }
  };

  const handleDisableProvision = async () => {
    if (!window.confirm('Disable Taptile? This will not delete your Taptile account — you can re-enable it at any time.')) return;
    setDisablingProvision(true);
    try {
      await api.delete('/integrations/taptile/provision');
      toast.success('Taptile disabled');
      await loadSettings();
    } catch {
      toast.error('Failed to disable');
    } finally {
      setDisablingProvision(false);
    }
  };

  const handleRetrieveKey = async () => {
    setRetrievingKey(true);
    try {
      const res = await api.post('/integrations/taptile/retrieve-api-key');
      if (res.data.already_connected) {
        toast.info('Already connected — reloading.');
      } else {
        toast.success('API key retrieved and connected automatically!');
      }
      await loadSettings();
      setEmbedState('idle');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not auto-retrieve API key.';
      toast.warning(msg);
      // Can't auto-retrieve — show manual entry form so user can paste the key
      setShowSettings(true);
    } finally {
      setRetrievingKey(false);
    }
  };

  /** Open portal with SSO when connected, fall back to plain portal_url otherwise */
  const handleOpenPortal = useCallback(async (portalUrl) => {
    if (!settings?.connected) {
      // No API key — open plain portal URL (requires manual sign-in)
      window.open(portalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setOpeningPortal(true);
    try {
      const res = await api.post('/integrations/taptile/embed-token');
      const ssoUrl = res.data.embed_url;
      window.open(ssoUrl, '_blank', 'noopener,noreferrer');
    } catch {
      // SSO failed — fall back to plain portal URL
      window.open(portalUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setOpeningPortal(false);
    }
  }, [settings]);

  const isLoading = settings === null;

  // ── FULL-VIEWPORT EMBED LAYOUT ──────────────────────────────────────────
  // When the iframe is ready, switch to a full-screen layout so there is
  // only ONE scroll surface (inside Taptile). The outer page is locked.
  if (embedState === 'ready' && embedUrl && !showSettings) {
    return (
      <div className="flex h-screen overflow-hidden" data-testid="taptile-app-page">
        <Sidebar />
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {/* Slim toolbar */}
          <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-slate-200 shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-md bg-indigo-600 flex items-center justify-center text-white shrink-0">
                <Smartphone size={14} />
              </div>
              <span className="text-sm font-semibold text-slate-800">Taptile</span>
              <span className="flex items-center gap-1 text-xs text-green-600 font-medium bg-green-50 border border-green-200 px-2 py-0.5 rounded-md">
                <CheckCircle size={11} /> Connected
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="ghost" onClick={() => window.open(embedUrl, '_blank', 'noopener,noreferrer')}
                className="text-slate-500 hover:text-slate-800" data-testid="taptile-open-new-tab">
                <ExternalLink size={13} className="mr-1" /> New tab
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowSettings(true)}
                className="text-slate-500 hover:text-slate-800" data-testid="taptile-toolbar-settings">
                <KeyRound size={13} className="mr-1" /> Settings
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDisconnect} disabled={disconnecting}
                className="text-red-400 hover:text-red-600 hover:bg-red-50" data-testid="taptile-toolbar-disconnect">
                {disconnecting ? <Loader2 size={13} className="animate-spin mr-1" /> : <Unplug size={13} className="mr-1" />}
                Disconnect
              </Button>
            </div>
          </div>
          {/* Iframe fills every remaining pixel — no outer scroll */}
          <iframe
            key={embedUrl}
            data-testid="taptile-embed-iframe"
            title="Taptile"
            src={embedUrl}
            className="flex-1 w-full border-none"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation-by-user-activation"
            allow="clipboard-read; clipboard-write"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex" data-testid="taptile-app-page">
      <Sidebar />
      <div className="flex-1 min-w-0 bg-slate-50 p-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center text-white">
              <Smartphone size={20} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Taptile</h1>
              <p className="text-sm text-slate-500">Manage your Taptile app and configure event push notifications.</p>
            </div>
          </div>

          {/* Connected manually (not via provisioning): show Update/Disconnect controls */}
          {settings?.connected && !showSettings && !settings?.provision_enabled && (
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium bg-green-50 border border-green-200 px-3 py-1.5 rounded-lg">
                <CheckCircle size={14} /> Connected
              </span>
              <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} data-testid="taptile-edit-btn">
                <KeyRound size={14} className="mr-1.5" /> Update API Key
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={disconnecting}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" data-testid="taptile-disconnect-btn">
                {disconnecting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Unplug size={14} className="mr-1.5" />}
                Disconnect
              </Button>
            </div>
          )}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="animate-spin" size={20} />
          </div>
        )}

        {/* ── PROVISIONING CARD ── */}
        {!isLoading && (() => {
          const plan = settings?.plan_level || 'freemium';
          const isPro = ['pro', 'enterprise', 'reseller'].includes(plan);
          const isProvisioned = settings?.provisioned;
          const isEnabled = settings?.provision_enabled;

          if (!isPro) {
            return (
              <div className="max-w-lg mb-6" data-testid="taptile-provision-locked">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 flex items-start gap-4">
                  <div className="w-9 h-9 rounded-lg bg-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                    <Lock size={16} className="text-slate-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-700 text-sm">Taptile Auto-Provisioning</p>
                    <p className="text-xs text-slate-500 mt-0.5">Automatically create your Taptile workspace with one click. Available on the <strong>Pro plan</strong> and above.</p>
                  </div>
                </div>
              </div>
            );
          }

          if (isProvisioned && isEnabled) {
            return (
              <div className="max-w-2xl mb-6" data-testid="taptile-provision-active">
                <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                        <Zap size={16} className="text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-green-800 text-sm flex items-center gap-2">
                          Taptile workspace active
                          {settings.short_code && (
                            <span className="bg-green-100 text-green-700 text-xs font-mono px-2 py-0.5 rounded" data-testid="taptile-short-code">
                              {settings.short_code}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-green-600 mt-0.5">Auto-provisioned — your account was created automatically. Use the portal link or the panel below to manage your app.</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {settings.portal_url && (
                        <button
                          onClick={() => handleOpenPortal(settings.portal_url)}
                          disabled={openingPortal}
                          className="flex items-center gap-1.5 text-xs text-green-700 font-medium bg-white border border-green-200 px-3 py-1.5 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-60"
                          data-testid="taptile-portal-link">
                          {openingPortal ? <Loader2 size={12} className="animate-spin" /> : <ExternalLink size={12} />}
                          {openingPortal ? 'Opening…' : 'Open Taptile Portal'}
                        </button>
                      )}
                      <button onClick={handleDisableProvision} disabled={disablingProvision}
                        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-red-600 transition-colors px-2 py-1.5"
                        data-testid="taptile-provision-disable-btn">
                        {disablingProvision ? <Loader2 size={14} className="animate-spin" /> : <ToggleRight size={18} className="text-green-500" />}
                        <span>Enabled</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          // Already manually connected (pre-provisioning) — don't show provisioning panel
          if (settings?.connected && !isProvisioned) {
            return null;
          }

          // Pro+ but not yet provisioned (or disabled)
          return (
            <div className="max-w-lg mb-6" data-testid="taptile-provision-panel">
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Zap size={16} className="text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800 text-sm">
                      {isProvisioned ? 'Taptile workspace (disabled)' : 'Auto-provision Taptile'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {isProvisioned
                        ? 'Your Taptile workspace exists but is currently disabled in NexusCMS. Re-enable to restore the connection.'
                        : 'Click Enable to automatically create your Taptile workspace. No separate signup required — the connection is fully automated.'}
                    </p>
                  </div>
                  <Button size="sm" onClick={handleProvision} disabled={provisioning}
                    className="shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="taptile-provision-btn">
                    {provisioning
                      ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Setting up…</>
                      : <><ToggleLeft size={14} className="mr-1.5" /> {isProvisioned ? 'Re-enable' : 'Enable Taptile'}</>}
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── PROVISIONED BUT NO API KEY ── */}
        {!isLoading && settings?.provisioned && settings?.provision_enabled && !settings?.connected && !showSettings && (() => {
          // Taptile only returns the API key for brand-new accounts (status=created).
          // For pre-existing accounts (status=existing) the key is omitted from the
          // provision response AND the GET /accounts/{id} endpoint returns 404.
          // Auto-connect is therefore only useful for new accounts where inline
          // retrieval failed for a transient reason.
          const isExistingAccount = settings?.provision_status === 'existing';
          return (
            <div className="max-w-2xl mb-6" data-testid="taptile-provision-no-key">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-4">
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                  <AlertCircle size={16} className="text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-amber-800 text-sm">
                    {isExistingAccount
                      ? 'Your existing Taptile account has been re-linked — one step to finish'
                      : 'One more step — enter your Taptile API key'}
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    {isExistingAccount
                      ? <>Because this is an existing Taptile account, the API key can't be retrieved automatically.
                          Open your{' '}
                          {settings?.portal_url
                            ? <button onClick={() => window.open(settings.portal_url, '_blank', 'noopener,noreferrer')} className="underline font-semibold hover:text-amber-900">Taptile Portal</button>
                            : <span className="font-semibold">Taptile Portal</span>
                          }
                          {' '}→ Settings → API Key, copy it, and paste it below.
                        </>
                      : <>Your workspace was just created. Find your API key in your{' '}
                          {settings?.portal_url
                            ? <button onClick={() => window.open(settings.portal_url, '_blank', 'noopener,noreferrer')} className="underline font-semibold hover:text-amber-900">Taptile Portal</button>
                            : <span className="font-semibold">Taptile Portal</span>
                          }
                          {' '}under Settings → API Key, or try Auto-connect below.
                        </>
                    }
                  </p>
                  <div className="flex items-center gap-3 mt-3">
                    <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={() => setShowSettings(true)}
                      data-testid="taptile-enter-key-btn">
                      <KeyRound size={13} className="mr-1.5" /> Enter API Key
                    </Button>
                    {!isExistingAccount && (
                      <Button size="sm" variant="outline" onClick={handleRetrieveKey} disabled={retrievingKey}
                        data-testid="taptile-auto-retrieve-btn">
                        {retrievingKey
                          ? <><Loader2 size={13} className="animate-spin mr-1.5" /> Trying…</>
                          : <><Zap size={13} className="mr-1.5" /> Auto-connect</>}
                      </Button>
                    )}
                    {settings?.portal_url && (
                      <button onClick={() => window.open(settings.portal_url, '_blank', 'noopener,noreferrer')}
                        className="flex items-center gap-1.5 text-xs text-amber-700 underline hover:text-amber-900"
                        data-testid="taptile-portal-link-amber">
                        <ExternalLink size={12} /> Open Taptile Portal
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── NOT CONNECTED (manual key — only for non-Pro tenants or manually-connected) ── */}
        {!isLoading && !settings?.connected && !showSettings && !settings?.provision_enabled &&
         !['pro', 'enterprise', 'reseller'].includes(settings?.plan_level || 'freemium') && (
          <div className="max-w-lg mx-auto" data-testid="taptile-not-connected">
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Plug size={26} className="text-slate-400" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">Connect your Taptile account</h2>
              <p className="text-sm text-slate-500 mb-6">
                Enter your Taptile API key below. This enables event push notifications and the in-app
                Taptile admin panel.
              </p>
              <Button onClick={() => setShowSettings(true)} className="bg-indigo-600 hover:bg-indigo-700 w-full" data-testid="taptile-connect-cta">
                <KeyRound size={16} className="mr-2" /> Enter API Key
              </Button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              <p className="font-medium mb-1">Where do I get my API key?</p>
              <p>Your Taptile API key is provided by Taptile when you sign up.
                Contact <a href="mailto:support@taptile.app" className="underline">support@taptile.app</a> if you need it resent.</p>
            </div>
          </div>
        )}

        {/* ── CONNECTION FORM ── */}
        {!isLoading && showSettings && (
          <div className="max-w-lg" data-testid="taptile-settings-form">
            <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-slate-900">
                  {settings?.connected ? 'Update Taptile Connection' : 'Connect Taptile'}
                </h2>
                <Button variant="ghost" size="sm" onClick={() => { setShowSettings(false); setApiKey(''); }}>
                  Cancel
                </Button>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">Taptile API Key *</Label>
                <p className="text-xs text-slate-400 mb-1.5">
                  Issued by Taptile to your school/organisation.
                  {settings?.api_key_masked && (
                    <span className="ml-1 text-slate-500">Current: <code className="bg-slate-100 px-1 rounded">{settings.api_key_masked}</code></span>
                  )}
                </p>
                <div className="relative">
                  <Input
                    type={showKey ? 'text' : 'password'}
                    placeholder={settings?.connected ? 'Paste new key to replace…' : 'Paste your Taptile API key'}
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                    className="pr-10"
                    data-testid="taptile-api-key-input"
                  />
                  <button type="button" onClick={() => setShowKey(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">Taptile Base URL</Label>
                <p className="text-xs text-slate-400 mb-1.5">Leave as default unless Taptile has given you a custom URL.</p>
                <Input
                  type="url"
                  value={apiUrl}
                  onChange={e => setApiUrl(e.target.value)}
                  placeholder="https://taptile.app"
                  data-testid="taptile-api-url-input"
                />
              </div>

              <div className="flex gap-3 pt-1">
                <Button onClick={handleSave} disabled={saving || !apiKey.trim()} className="flex-1" data-testid="taptile-save-btn">
                  {saving ? <Loader2 size={15} className="animate-spin mr-2" /> : <CheckCircle size={15} className="mr-2" />}
                  {settings?.connected ? 'Update Key' : 'Connect Taptile'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── EMBED (loading / error states only — ready state uses full-viewport layout above) ── */}
        {!isLoading && settings?.connected && !showSettings && (
          <>
            {embedState === 'loading' && (
              <div className="flex items-center justify-center py-16 text-slate-400" data-testid="taptile-embed-loading">
                <Loader2 className="animate-spin mr-2" size={18} /> Opening Taptile…
              </div>
            )}

            {embedState === 'error' && (
              <div className="bg-white border border-slate-200 rounded-xl p-8 text-center max-w-lg" data-testid="taptile-embed-error">
                <p className="text-slate-600 mb-1 text-sm font-medium">Could not load the Taptile panel</p>
                <p className="text-slate-400 text-xs mb-4">{embedError}</p>
                <Button variant="outline" onClick={loadEmbed} data-testid="taptile-embed-retry">
                  <RefreshCw size={15} className="mr-2" /> Retry
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TaptileApp;
